<template>
  <!-- FI-2182: the passkeys rows of the profile page. a tbody rather than a
       div because user_detail lays the page out as one ud-form-table and
       these rows keep its shape: the section title row, then one row whose
       control spans the label column too (the list wants the width, and the
       title already says what it is) with the help copy on the right. in
       admin mode nothing renders until the list is in, and a customer
       account with no passkeys gets no section at all: there is nothing to
       manage. -->
  <tbody v-if="visible" class="ud-passkeys">
    <tr>
      <td colspan="3" class="ud-section-cell">
        <span class="ud-section-title">Passkeys</span>
        <hr class="ud-section-rule"/>
      </td>
    </tr>
    <tr>
      <td colspan="2" valign="top" class="ud-control pk-control">
        <!-- first load only: a reload after an action keeps the rows in place -->
        <div v-if="loading && !loaded" class="pk-muted pk-loading">Loading passkeys...</div>
        <div v-else-if="loadError" class="pk-muted pk-load-error">{{ loadError }}</div>
        <template v-else>
          <ul v-if="passkeys.length" class="pk-list">
            <li v-for="row in passkeys" :key="row.credential_id" class="pk-row">
              <span class="pk-row-icon" aria-hidden="true"><i class="fa fa-key"></i></span>
              <div class="pk-row-main">
                <div class="pk-row-name">
                  <strong>{{ row.nickname || 'Passkey' }}</strong>
                  <span v-if="isSelf && row.credential_id === sessionId" class="pk-badge">This session</span>
                </div>
                <div class="pk-row-meta">
                  <span>Added {{ formatDate(row.created_at) }}</span>
                  <span class="pk-meta-sep" aria-hidden="true">&middot;</span>
                  <span v-if="row.last_used_at">Last used {{ formatDate(row.last_used_at) }}</span>
                  <span v-else>Never used</span>
                </div>
              </div>
              <div class="pk-row-actions">
                <!-- two-click confirm, inline. window.confirm is out: safari
                     swallowed it in the harness. -->
                <template v-if="confirmingId === row.credential_id">
                  <span class="pk-confirm-text">Remove this passkey?</span>
                  <b-button size="sm" variant="danger" class="pk-btn-confirm" :disabled="busy" @click="removeConfirmed(row)">Remove</b-button>
                  <b-button size="sm" variant="outline-secondary" class="pk-btn-keep" :disabled="busy" @click="cancelRemove">Keep</b-button>
                </template>
                <template v-else>
                  <b-button v-if="isSelf" size="sm" variant="outline-secondary" class="pk-btn-replace" :disabled="busy" @click="replacePasskey(row)">Replace</b-button>
                  <b-button size="sm" variant="outline-danger" class="pk-btn-remove" :disabled="busy" @click="askRemove(row)">Remove</b-button>
                </template>
              </div>
            </li>
          </ul>
          <div v-else class="pk-muted pk-empty">{{ isSelf ? 'No passkeys yet.' : 'No passkeys.' }}</div>
        </template>
        <div v-if="isSelf" class="pk-add">
          <b-button v-if="supported" size="sm" variant="primary" id="ud-passkey-add" :disabled="busy || loading" @click="addPasskey"><i class="fa fa-plus" aria-hidden="true"></i> Add</b-button>
          <span v-else class="pk-muted pk-unsupported">Your browser does not support passkeys.</span>
        </div>
      </td>
      <td valign="top" class="ud-help">
        <span v-if="isSelf">Sign in with Touch ID, Face ID, Windows Hello or a security key instead of typing your password. Your password keeps working. Add one passkey per device you sign in from.</span>
        <span v-else>Passkeys this user has set up. Remove one if the device was lost or replaced; they can still sign in with their password and add a new passkey.</span>
      </td>
    </tr>
  </tbody>
</template>

<script>
import {
  get_passkeys, passkey_register_begin, passkey_register_complete, delete_passkey,
  get_user_passkeys, delete_user_passkey,
} from '../networking/apis'
import {
  webauthnSupported, createPasskey, passkeyErrorMessage,
  jwtClaims, sessionPasskeyId, rememberPasskeyOnThisBrowser,
} from '../utils/passkeys'

export default {
  name: 'passkeys_section',
  props: {
    // the account whose passkeys these are. in self mode the api reads it
    // off the jwt; in admin mode it names the route.
    username: { type: String, default: '' },
    // 'self': add, replace, remove your own. 'admin': remove from any account
    // (the lost-device recovery), nothing else.
    mode: { type: String, default: 'self', validator: v => v === 'self' || v === 'admin' },
    // the role of the account being viewed. an admin target keeps the section
    // even when empty so a fellow admin can see there is nothing to remove.
    targetRole: { type: String, default: '' },
  },
  data () {
    return {
      passkeys: [],
      loading: false,
      // true once the first load has settled, success or not
      loaded: false,
      loadError: null,
      // an add, replace or remove in flight: every action button waits on it
      busy: false,
      // the row whose actions read "Remove this passkey?", one at a time
      confirmingId: null,
      // the pk claim of the current jwt, refreshed on every load so a token
      // handoff moves the badge
      sessionId: null,
      // a load that started before a username change is dropped on arrival
      loadSeq: 0,
    }
  },
  computed: {
    isSelf() {
      return this.mode !== 'admin'
    },
    supported() {
      return webauthnSupported(window)
    },
    visible() {
      if (this.isSelf) return true
      if (!this.loaded) return false
      return this.passkeys.length > 0 || this.targetRole === 'admin'
    },
  },
  watch: {
    username() {
      // a different account: nothing of the previous one should show
      this.passkeys = []
      this.loaded = false
      this.load()
    },
  },
  created() {
    this.load()
  },
  methods: {
    formatDate(iso) {
      const d = new Date(iso || '')
      if (isNaN(d.getTime())) return 'unknown date'
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },
    // a reply that carries token is the api moving this session off the
    // passkey it was started with (replaced or removed). same keys
    // login.vue's writeSession writes; everything else in storage stays.
    adoptToken(token) {
      if (!token) return
      try {
        localStorage.setItem('jwt', token)
        const claims = jwtClaims(token)
        if (claims && claims.exp != null) localStorage.setItem('expiration_token_time', claims.exp)
      } catch (_) { /* storage blocked */ }
    },
    async load() {
      const seq = ++this.loadSeq
      this.loading = true
      this.loadError = null
      try {
        this.sessionId = sessionPasskeyId(localStorage.getItem('jwt'))
      } catch (_) {
        this.sessionId = null
      }
      try {
        const { value: { data } } = this.isSelf
          ? await get_passkeys()
          : await get_user_passkeys(this.username)
        if (seq !== this.loadSeq) return
        // the api sorts by created_at already; keep that order on our side too
        this.passkeys = ((data && data.passkeys) || []).slice()
          .sort((a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')))
      } catch (e) {
        if (seq !== this.loadSeq) return
        this.passkeys = []
        this.loadError = passkeyErrorMessage(e, 'list')
      } finally {
        if (seq === this.loadSeq) {
          this.loading = false
          this.loaded = true
        }
      }
    },
    askRemove(row) {
      if (this.busy) return
      this.confirmingId = row.credential_id
    },
    cancelRemove() {
      this.confirmingId = null
    },
    async removeConfirmed(row) {
      if (this.busy) return
      this.busy = true
      try {
        const { value: { data } } = this.isSelf
          ? await delete_passkey(row.credential_id)
          : await delete_user_passkey(this.username, row.credential_id)
        this.adoptToken(data && data.token)
        this.$emit('showAlert', 'Passkey removed', 'success', 1500)
      } catch (e) {
        this.$emit('showAlert', passkeyErrorMessage(e, 'remove'), 'danger', 6000)
      } finally {
        this.confirmingId = null
        await this.load()
        this.busy = false
      }
    },
    addPasskey() {
      return this.enroll(null)
    },
    replacePasskey(row) {
      return this.enroll(row.credential_id)
    },
    // the registration ceremony: options from the api, the browser prompt,
    // the credential back to the api. a replace is the same three steps with
    // the old credential named up front so the api swaps rather than adds.
    // no nickname goes up: the api names the passkey after the service that
    // holds it (iCloud Keychain, Google Password Manager, Windows Hello...)
    // from the authenticator id in the attestation, which the browser does
    // not hand to page script.
    async enroll(replaceId) {
      if (this.busy) return
      this.busy = true
      this.confirmingId = null
      const action = replaceId ? 'replace' : 'add'
      try {
        const body = replaceId ? { replace_credential_id: replaceId } : {}
        const { value: { data: begun } } = await passkey_register_begin(body)
        const credential = await createPasskey(begun.options, window)
        const { value: { data: added } } = await passkey_register_complete({
          challenge_token: begun.challenge_token,
          credential,
        })
        this.adoptToken(added && added.token)
        rememberPasskeyOnThisBrowser(localStorage)
        this.$emit('showAlert', replaceId ? 'Passkey replaced' : 'Passkey added', 'success', 1500)
      } catch (e) {
        // null means the user closed the prompt: nothing to say
        const msg = passkeyErrorMessage(e, action)
        if (msg) this.$emit('showAlert', msg, 'warning', 6000)
      } finally {
        await this.load()
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
/* the section classes mirror user_detail.vue: its styles are scoped and stop
   at this component's root, so the same rules are repeated here (desktop
   widths above, the narrow-viewport placement below). keep the two in step.
   the tr/td grid itself comes through from user_detail's ::v-deep rules and
   needs no copy. */
.ud-section-cell {
  padding-top: 20px;
}
.ud-section-title {
  color: steelblue;
  font-weight: bold;
  font-size: 20px;
}
.ud-section-rule {
  margin: 5px 0 15px 0;
}
.ud-control {
  font-weight: normal;
}
/* spans the label and control columns of the page (280 + 460) */
.pk-control {
  width: 740px;
}
.ud-help {
  width: 320px;
  font-size: 12px;
  color: #888;
  font-weight: normal;
}

/* the list: one bordered row per passkey, a key glyph, the name and dates,
   the buttons on the right. no fill behind the rows so the dark skin is not
   handed a light island; the glyph tint is the brand blue at low alpha for
   the same reason. */
.pk-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.pk-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
}
.pk-row + .pk-row {
  margin-top: 8px;
}
.pk-row-icon {
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: rgba(25, 118, 210, 0.12);
  color: #1976D2;
  font-size: 15px;
}
.pk-row-main {
  flex: 1 1 auto;
  min-width: 0;
}
.pk-row-name {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 15px;
  line-height: 1.3;
}
.pk-row-meta {
  font-size: 13px;
  color: #888;
  margin-top: 2px;
}
.pk-meta-sep {
  margin: 0 5px;
}
.pk-badge {
  display: inline-block;
  padding: 1px 6px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  color: #6c757d;
  background: #eef0f3;
  border: 1px solid #d5d9de;
  border-radius: 4px;
  white-space: nowrap;
}
.pk-row-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
  flex: none;
}
.pk-confirm-text {
  font-size: 13px;
  color: #6c757d;
}
.pk-muted {
  font-size: 13px;
  color: #888;
}
.pk-add {
  margin-top: 12px;
}
.pk-add .fa {
  margin-right: 4px;
}

/* narrow viewports: the page turns each tr into a two-column grid. this row
   has no label cell, so the list takes the full width on the first grid row
   and the help copy sits under it, the way the page places its own help. */
@media (max-width: 900px) {
  .ud-section-cell {
    grid-column: 1 / -1;
  }
  .pk-control {
    grid-column: 1 / -1;
    grid-row: 1;
    width: auto;
  }
  .ud-help {
    grid-column: 1 / -1;
    grid-row: 2;
    padding-top: 2px;
    width: auto;
  }
  .pk-row {
    flex-wrap: wrap;
  }
  .pk-row-actions {
    flex: 1 1 100%;
    justify-content: flex-start;
    padding-left: 46px;
  }
}
</style>
